import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PageHeader from '../../components/PageHeader';

const UsersPage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [timeRange, setTimeRange] = useState('7d');
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [newUsersCount, setNewUsersCount] = useState(0);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchUsers();
  }, [page]);

  useEffect(() => {
    fetchChartData();
  }, [timeRange]);

  const fetchChartData = async () => {
    setLoadingChart(true);
    try {
      const now = new Date();
      let startDate = new Date();
      let groupFormat = 'hour';
      
      switch (timeRange) {
        case '24h':
          startDate.setHours(now.getHours() - 24);
          groupFormat = 'hour';
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          groupFormat = 'day';
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          groupFormat = 'day';
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          groupFormat = 'day';
          break;
        default:
          startDate.setDate(now.getDate() - 7);
      }

      // Récupérer les utilisateurs créés dans la période
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, created_at')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (profilesError) throw profilesError;

      // Récupérer les rôles des utilisateurs
      const userIds = profilesData.map(p => p.id);
      const { data: rolesData, error: rolesError } = await supabase
        .from('roles')
        .select('id, role')
        .in('id', userIds);

      if (rolesError) throw rolesError;

      // Créer un map des rôles
      const rolesMap = {};
      rolesData.forEach(r => {
        rolesMap[r.id] = r.role;
      });

      // Compter les nouveaux utilisateurs dans la période
      setNewUsersCount(profilesData.length);

      // Grouper les données par période
      const grouped = {};
      
      profilesData.forEach(user => {
        const date = new Date(user.created_at);
        let key;
        
        if (groupFormat === 'hour') {
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:00`;
        } else {
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }

        if (!grouped[key]) {
          grouped[key] = {
            date: key,
            total: 0,
            organizers: 0
          };
        }
        
        grouped[key].total++;
        if (rolesMap[user.id] === 'organizer') {
          grouped[key].organizers++;
        }
      });

      // Convertir en tableau
      const chartArray = Object.values(grouped).map(item => ({
        date: item.date,
        [t('settings.admin.users.chart.newUsers')]: item.total,
        [t('settings.admin.users.chart.newOrganizers')]: item.organizers
      }));

      setChartData(chartArray);
    } catch (error) {
      console.error('Erreur lors du chargement des données du graphique:', error);
    } finally {
      setLoadingChart(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error, count } = await supabase
        .from('profiles')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((page - 1) * itemsPerPage, page * itemsPerPage - 1);

      if (error) throw error;
      
      // Récupérer les rôles et crédits pour chaque utilisateur
      const usersWithDetails = await Promise.all(
        (data || []).map(async (user) => {
          const [{ data: creditsData }, { data: roleData }] = await Promise.all([
            supabase
              .from('credits')
              .select('credits')
              .eq('id', user.id)
              .single(),
            supabase
              .from('roles')
              .select('role')
              .eq('id', user.id)
              .single()
          ]);
          
          return {
            ...user,
            credits: creditsData?.credits || 0,
            role: roleData?.role || 'user'
          };
        })
      );
      
      setUsers(usersWithDetails);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: t('roles.admin'), color: 'bg-[#2894D8]' },
      admin_local: { label: t('roles.adminLocal'), color: 'bg-blue-500' },
      organizer: { label: t('roles.organizer'), color: 'bg-green-500' },
      user: { label: t('roles.user'), color: 'bg-gray-500' }
    };
    
    const config = roleConfig[role] || roleConfig.user;
    
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full text-white ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2894D8]"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <PageHeader title={t('settings.admin.users.title')} backPath="/settings" />

      <div className="p-4">
        {/* Graphique */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <h2 className="text-h2 mb-4">{t('settings.admin.users.chart.title')}</h2>
          
          {/* Filtres temporels */}
          <div className="flex gap-2 overflow-x-auto mb-4">
            <button
              onClick={() => setTimeRange('24h')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '24h'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('settings.admin.visits.chart.24h')}
            </button>
            <button
              onClick={() => setTimeRange('7d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '7d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('settings.admin.visits.chart.7d')}
            </button>
            <button
              onClick={() => setTimeRange('30d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '30d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('settings.admin.visits.chart.30d')}
            </button>
            <button
              onClick={() => setTimeRange('90d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '90d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('settings.admin.visits.chart.90d')}
            </button>
          </div>

          {/* Graphique */}
          {loadingChart ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2894D8]"></div>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500">{t('settings.admin.visits.chart.noData')}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={t('settings.admin.users.chart.newUsers')}
                  stroke="#2894D8" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line 
                  type="monotone" 
                  dataKey={t('settings.admin.users.chart.newOrganizers')}
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Statistiques */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('settings.admin.users.totalUsers')}</p>
              <p className="text-2xl font-bold text-[#2894D8]">{totalCount}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('settings.admin.users.newUsers')}</p>
              <p className="text-2xl font-bold text-[#10B981]">{newUsersCount}</p>
            </div>
          </div>
        </div>

        {/* Liste des utilisateurs */}
        {loading ? (
          <div className="text-center py-8">
            <p>{t('common.loading')}...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <p className="text-gray-500">{t('settings.admin.users.noUsers')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {users.map((user) => (
              <div key={user.id} className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm">
                        {user.organizer_name || user.display_name || t('settings.admin.users.noName')}
                      </p>
                      {getRoleBadge(user.role)}
                    </div>
                    <p className="text-xs text-gray-500 break-all">{user.id}</p>
                  </div>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>📅 {t('settings.admin.users.createdAt')} {formatDate(user.created_at)}</p>
                  {user.credits !== null && user.credits !== undefined && (
                    <p>💰 {t('settings.admin.users.credits')} {user.credits}</p>
                  )}
                  {user.organizer_website && (
                    <p className="break-all">🌐 {user.organizer_website}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg bg-white shadow-sm disabled:opacity-50"
            >
              {t('common.previous')}
            </button>
            <span className="px-4 py-2">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-lg bg-white shadow-sm disabled:opacity-50"
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
